import type { ScanAggregates } from '@utils/scanRunCharts';

/** One content-category group, as the API sends it (REV-715). */
export type CategoryGroup = ScanAggregates['byCategory']['categories'][number];
/** One rule within a group. */
export type RuleGroup = CategoryGroup['rules'][number];
/** The severities a scan finding can carry — the page's severity filter. */
export type GroupSeverity = Exclude<keyof RuleGroup['bySeverity'], 'unspecified'>;

/** The API's bucket for rules the catalog does not know; it always sorts last. */
const UNCATEGORIZED = 'Uncategorized';

/**
 * REV-715: apply the page's filters to the API's grouping.
 *
 * Counts are recomputed from each rule's `bySeverity` rather than requested
 * again, and anything a filter empties is removed rather than shown as
 * "0 issues" — AFixt-Investigator shows empty groups, and a reader has to open
 * them to learn there is nothing inside. The result keeps the API's order
 * rules: heaviest first, `Uncategorized` last, ties by name.
 *
 * With no filter the grouping is returned exactly as sent.
 */
export function filterGroups(
  categories: readonly CategoryGroup[],
  { severity, ruleId }: { severity?: GroupSeverity; ruleId?: string }
): CategoryGroup[] {
  if (!severity && !ruleId) {
    return [...categories];
  }
  return categories
    .map((category) => {
      const rules = category.rules
        .filter((rule) => !ruleId || rule.ruleId === ruleId)
        .map((rule) => (severity ? { ...rule, findings: rule.bySeverity[severity] } : rule))
        .filter((rule) => rule.findings > 0)
        .sort((a, b) => b.findings - a.findings || a.ruleId.localeCompare(b.ruleId));
      return { ...category, rules, findings: rules.reduce((n, r) => n + r.findings, 0) };
    })
    .filter((category) => category.findings > 0)
    .sort(
      (a, b) =>
        Number(a.category === UNCATEGORIZED) - Number(b.category === UNCATEGORIZED) ||
        b.findings - a.findings ||
        a.category.localeCompare(b.category)
    );
}
